import classNames from 'classnames';
import React, { forwardRef } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Pill from '../pill/pill';
import styles from './radiobutton.module.scss';

export interface RadiobuttonProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Indicates whether the field is checked */
  checked?: boolean;
  /** The number of items selected */
  count?: number;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field has an error */
  error?: boolean;
  /** Identification for the radio button */
  id?: string;
  /** The text that is shown to the right of the field */
  label?: string;
  /** An event that is triggered on change */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** An event that is triggered on click */
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  /** The title that is shown on top of the radio button */
  title?: string;
  /** The value of the field */
  value?: string;
}

const Radiobutton = forwardRef<HTMLInputElement, RadiobuttonProps>(
  (
    {
      checked,
      children,
      className,
      count,
      disabled,
      error,
      id,
      label,
      required,
      title,
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        className={classNames(styles.component, className, {
          [styles.hasError]: error,
          [styles.isChecked]: checked,
          [styles.isDisabled]: disabled,
        })}
      >
        {title && (
          <Typography
            className={styles.title}
            htmlFor={id}
            tag="label"
            tagStyle="bodySmallBold"
          >
            {title}

            {required && <span className={styles.requiredMark}> *</span>}
          </Typography>
        )}

        <label className={styles.label}>
          <span className={styles.inputWrapper}>
            <input
              {...rest}
              ref={ref}
              checked={checked}
              className={styles.input}
              disabled={disabled}
              id={id}
              type="radio"
            />

            <span className={styles.field} />

            {(label || children) && (
              <span>
                <Typography tagStyle="bodyMedium">
                  {label}

                  {children}
                </Typography>
              </span>
            )}
          </span>

          {typeof count === 'number' && <Pill count={count} />}
        </label>
      </div>
    );
  },
);

Radiobutton.displayName = 'Radiobutton';

export default Radiobutton;
